import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { API } from '../App';

// Two public pages: "Forgot password" asks for an email address, and
// "Reset password" sets a new password from the one-time link in that email.

const styles = {
  container: {
    minHeight: '100vh', backgroundColor: '#0f0f1a', display: 'flex',
    alignItems: 'center', justifyContent: 'center', padding: '20px',
  },
  card: { backgroundColor: '#16213e', borderRadius: '16px', padding: '40px', width: '100%', maxWidth: '420px' },
  brand: { color: '#e94560', fontSize: '26px', fontWeight: 'bold', letterSpacing: '2px', textAlign: 'center' },
  sub: { color: '#a0a0b0', fontSize: '13px', textAlign: 'center', marginBottom: '28px' },
  title: { color: '#ffffff', fontSize: '20px', fontWeight: 'bold', marginBottom: '8px' },
  text: { color: '#a0a0b0', fontSize: '14px', lineHeight: 1.5, marginBottom: '20px' },
  label: { color: '#a0a0b0', fontSize: '12px', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px', display: 'block' },
  input: {
    width: '100%', padding: '12px 14px', backgroundColor: '#0f3460', border: '1px solid #0f3460',
    borderRadius: '8px', color: '#ffffff', fontSize: '15px', outline: 'none', boxSizing: 'border-box', marginBottom: '16px',
  },
  button: {
    width: '100%', padding: '14px', backgroundColor: '#e94560', border: 'none', borderRadius: '8px',
    color: '#ffffff', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer',
  },
  link: { color: '#e94560', fontWeight: 'bold', textDecoration: 'none' },
  footer: { color: '#a0a0b0', fontSize: '13px', textAlign: 'center', marginTop: '20px' },
  error: { color: '#e94560', fontSize: '14px', marginBottom: '16px' },
  success: { color: '#00cc00', fontSize: '14px', lineHeight: 1.5, marginBottom: '16px' },
  notice: {
    backgroundColor: 'rgba(255, 204, 0, 0.12)', border: '1px solid #ffcc00', borderRadius: '8px',
    color: '#ffcc00', fontSize: '13px', padding: '10px 14px', marginBottom: '16px', lineHeight: 1.5,
  },
};

export function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [emailWorks, setEmailWorks] = useState(true);

  useEffect(() => {
    fetch(`${API}/auth/email-status`)
      .then(r => r.json())
      .then(d => setEmailWorks(d.email_configured !== false))
      .catch(() => {});
  }, []);

  async function submit() {
    if (!email.trim()) { setError('Enter your email address'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.ok) setSent(true);
      else setError('Something went wrong. Please try again.');
    } catch (e) {
      setError('Could not connect to server');
    }
    setBusy(false);
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.brand}>EZ PACE</div>
        <div style={styles.sub}>Time Motion System</div>
        {sent ? (
          <>
            <div style={styles.title}>Check your email</div>
            <div style={styles.success}>
              If that email address has an account, a reset link is on its way. The link works once and expires in
              an hour.
            </div>
            <div style={styles.text}>
              Nothing arriving? Check the junk folder, or ask your casino's EZ Pace admin to reset it for you.
            </div>
            <Link to="/login" style={styles.link}>Back to sign in</Link>
          </>
        ) : (
          <>
            <div style={styles.title}>Forgot your password?</div>
            <div style={styles.text}>Enter the email address you sign in with and we'll send you a reset link.</div>
            {!emailWorks && (
              <div style={styles.notice}>
                Email sending is not set up on this server yet, so a link cannot be delivered. Please contact EZ Pace
                support to have your password reset.
              </div>
            )}
            {error && <div style={styles.error}>{error}</div>}
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }}
              placeholder="you@casino.com"
            />
            <button style={styles.button} onClick={submit} disabled={busy}>
              {busy ? 'Sending...' : 'Send reset link'}
            </button>
            <div style={styles.footer}>
              <Link to="/login" style={styles.link}>Back to sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  async function submit() {
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    if (password !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true);
    setError('');
    try {
      const res = await fetch(`${API}/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setDone(true);
        setTimeout(() => navigate('/login'), 3000);
      } else {
        setError(typeof data.detail === 'string' ? data.detail : 'Could not reset the password');
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setBusy(false);
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.brand}>EZ PACE</div>
        <div style={styles.sub}>Time Motion System</div>
        {done ? (
          <>
            <div style={styles.title}>Password changed</div>
            <div style={styles.success}>You can now sign in with your new password. Taking you to sign in...</div>
            <Link to="/login" style={styles.link}>Sign in now</Link>
          </>
        ) : !token ? (
          <>
            <div style={styles.title}>Link not valid</div>
            <div style={styles.text}>This page needs the link from your reset email.</div>
            <Link to="/forgot-password" style={styles.link}>Request a new link</Link>
          </>
        ) : (
          <>
            <div style={styles.title}>Choose a new password</div>
            <div style={styles.text}>At least 8 characters. You'll use it to sign in to EZ Pace.</div>
            {error && <div style={styles.error}>{error}</div>}
            <label style={styles.label}>New password</label>
            <input style={styles.input} type="password" value={password}
              onChange={e => setPassword(e.target.value)} placeholder="New password" />
            <label style={styles.label}>Confirm password</label>
            <input style={styles.input} type="password" value={confirm}
              onChange={e => setConfirm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') submit(); }} placeholder="Re-enter password" />
            <button style={styles.button} onClick={submit} disabled={busy}>
              {busy ? 'Saving...' : 'Set new password'}
            </button>
            <div style={styles.footer}>
              <Link to="/login" style={styles.link}>Back to sign in</Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
